import type { AspectRatio } from '@studio/shared';

interface Props {
  ratios: string[];
  selected: AspectRatio;
  onChange: (ratio: AspectRatio) => void;
}

/**
 * Aspect ratio, drawn at its actual proportions.
 *
 * "4:5" and "2:3" are two pairs of numbers that look almost identical in a
 * dropdown and are obviously different as shapes — so show the shape and let
 * the numbers annotate it.
 */
export function RatioPicker({ ratios, selected, onChange }: Props) {
  return (
    <div className="ratios">
      {ratios.map((ratio) => {
        const [w, h] = ratio.split(':').map(Number) as [number, number];
        // Normalise so every swatch fits the same box, tallest or widest edge.
        const scale = 46 / Math.max(w, h);
        return (
          <button
            key={ratio}
            type="button"
            className={`ratio ${selected === ratio ? 'is-on' : ''}`}
            aria-pressed={selected === ratio}
            onClick={() => onChange(ratio as AspectRatio)}
          >
            <span className="ratio__box">
              <span className="ratio__shape" style={{ width: w * scale, height: h * scale }} />
            </span>
            <span className="ratio__label">{ratio}</span>
            <span className="ratio__use">{USES[ratio] ?? ''}</span>
          </button>
        );
      })}
    </div>
  );
}

/** What each shape is actually for, since the numbers do not say. */
const USES: Record<string, string> = {
  '2:3': 'editorial',
  '3:4': 'catalogue',
  '4:5': 'social feed',
  '1:1': 'square',
  '4:3': 'landscape',
  '16:9': 'banner',
  '9:16': 'story',
};
